import type { CreateSaunaSetupNotePayload, DeleteSaunaSetupNotePayload, UpdateSaunaSetupNotePayload } from '@jums-dashboard/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { createSaunaSetupNote, deleteSaunaSetupNote, updateSaunaSetupNote } from '../services/saunaService';
import { saunaSessionHistoryQueryKey } from './useSaunaSessionHistory';

function useSetupNoteMutation<TPayload extends { homeId: string }, TResult>(
  fn: (payload: TPayload) => Promise<TResult>,
  successMessage: string,
  failureMessage: string,
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TPayload>({
    mutationFn: fn,
    onSuccess: (_data, { homeId }) => {
      toast.success(successMessage);
      void qc.invalidateQueries({ queryKey: saunaSessionHistoryQueryKey(homeId) });
    },
    onError: (err) => {
      toast.error(err.message || failureMessage);
    },
  });
}

export function useCreateSaunaSetupNote() {
  return useSetupNoteMutation<CreateSaunaSetupNotePayload, { noteId: string }>(
    createSaunaSetupNote,
    'Setup note added.',
    'Failed to add the setup note.',
  );
}

export function useUpdateSaunaSetupNote() {
  return useSetupNoteMutation<UpdateSaunaSetupNotePayload, { ok: true }>(
    updateSaunaSetupNote,
    'Setup note saved.',
    'Failed to save the setup note.',
  );
}

export function useDeleteSaunaSetupNote() {
  return useSetupNoteMutation<DeleteSaunaSetupNotePayload, { ok: true }>(
    deleteSaunaSetupNote,
    'Setup note deleted.',
    'Failed to delete the setup note.',
  );
}
